/*
 * Copyright (c) 2024 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { useContext } from "react";
import { ContextUpdater, DataContext } from "../../Context";
import { useNavigate } from "react-router-dom";
import { Button, Label } from "reactstrap";
import { formatDate, unixEpochToString } from "../../util/DateTime";
import { localizations } from "../../util/Localization";
import { useUpdateState } from "../../util/State";
import {
  requestClientList,
  requestDeleteClientData,
  requestOptInClientData,
  requestOptOutClientData,
  requestUpdateClientData
} from "../../requests/data/Requests";
import ScrollableDiv from "../../elements/containers/ScrollableDiv";
import DataLabel from "./DataLabel";
import ModalDataUpdader from "./ModalDataUpdater";
import ConfirmModal from "../../elements/modal/ConfirmModal";

const DataView = () => {
  // contexts
  const contextUpdater = useContext(ContextUpdater);
  const {
    clientId,
    guestId,
    clientName,
    optedIn,
    name,
    email,
    phoneNumber,
    birthday,
    language,
    timezone,
    activity,
  } = useContext(DataContext);
  const availableActivity = !!activity?.length;

  // state
  const initialState = { updateOpen: false, optInOpen: false, optOutOpen: false, deleteOpen: false };
  const [state, updateState] = useUpdateState(initialState);

  // navigation
  const navigate = useNavigate();

  // handlers
  const handleBack = () => {
    requestClientList({
      context: contextUpdater,
      onSuccess: () => navigate("/dashboard")
    });
  };

  const handleUpdate = (data) => {
    requestUpdateClientData({
      ...data,
      context: contextUpdater,
      clientId: clientId,
      guestId: guestId,
    });
  };

  const handleOptIn = () => {
    requestOptInClientData({
      context: contextUpdater,
      clientId: clientId,
      guestId: guestId,
    });
  };

  const handleOptOut = () => {
    requestOptOutClientData({
      context: contextUpdater,
      clientId: clientId,
      guestId: guestId,
    });
  };

  const handleDelete = () => {
    requestDeleteClientData({
      context: contextUpdater,
      clientId: clientId,
      guestId: guestId,
      onSuccess: () => navigate("/dashboard")
    });
  };

  // helpers
  const formatBirthday = (birthday) => {
    if (!birthday) {
      return null;
    }
    return formatDate(birthday.day, birthday.month, birthday.year);
  };

  // styles
  const containerStyle = {
    margin: "20px",
    border: "1px solid #8883",
    backgroundColor: "#aaa1",
    borderRadius: "10px",
  };

  return (
    <div className="fill-container flex-dynamic d-flex flex-column">
      <div className="m-1 flex-static d-flex">
        <Button outline size="sm" color="dark" onClick={handleBack}>{"< Back"}</Button>
      </div>
      {optedIn === false ? (
        <Label className="flex-static text-center pb-5" tag="h4">
          You have no sensitive data stored with <strong>{clientName}</strong>
        </Label>
      ) : (
        <>
          <Label className="flex-static text-center" tag="h1">
            <strong>{clientName}</strong> Data
          </Label>
          <div className="flex-dynamic d-flex">
            <div className="flex-dynamic d-flex" style={containerStyle}>
              <ScrollableDiv hideScroll className="fill-container d-flex flex-column">
                <Label className="my-3 w-100 text-center" tag="h3">Personal Information</Label>
                <DataLabel title="Name" value={name} />
                <DataLabel title="Email" value={email} />
                <DataLabel title="Phone Number" value={phoneNumber} />
                <DataLabel title="Birthday" value={formatBirthday(birthday)} />
                <DataLabel title="Language" value={localizations.find(l => l.id === language)?.name} />
                <DataLabel title="Timezone" value={timezone} />
                <div className="pt-3" align="center">
                  <Button color="green" onClick={() => updateState({ updateOpen: true })}>
                    Update Information
                  </Button>
                </div>
              </ScrollableDiv>
            </div>
            <div className="flex-dynamic d-flex" style={containerStyle}>
              <ScrollableDiv hideScroll>
                <Label className="my-3 w-100 text-center" tag="h3">Recorded Activity</Label>
                <div className="fill-container d-flex flex-column">
                  {availableActivity ? activity.map(({type, timestamp}, index) => (
                    <Label className="px-2" key={index}>
                      <span>{userActivityTypes[type]?.description ?? "Unknown"} at </span>
                      <strong>{unixEpochToString({timestamp})}</strong>
                    </Label>
                  )) : (
                    <Label className="text-muted">No activity recorded</Label>
                  )}
                </div>
              </ScrollableDiv>
            </div>
          </div>
        </>
      )}
      <div className="mx-3 mb-3 flex-static d-flex justify-content-center">
        {optedIn === false && (
          <Button className="mx-1" color="blue" onClick={() => updateState({ optInOpen: true })} style={{ width: "160px" }}>
            GDPR Opt In
          </Button>
        )}
        {optedIn === true && (
          <Button className="mx-1" color="blue" onClick={() => updateState({ optOutOpen: true })} style={{ width: "160px" }}>
            GDPR Opt Out
          </Button>
        )}
        <Button className="mx-1" color="red" onClick={() => updateState({ deleteOpen: true })} style={{ width: "160px" }}>
          Delete Account
        </Button>
      </div>
      <ModalDataUpdader
        isOpen={state.updateOpen}
        onSubmit={handleUpdate}
        onDone={() => updateState({ updateOpen: false })}
      />
      <ConfirmModal
        isOpen={state.optInOpen}
        toggle={() => updateState({ optInOpen: false })}
        size="lg"
        confirmColor="blue"
        onConfirm={handleOptIn}
      >
        <div align="center">
          <Label tag="h2">Confirm GDPR Opt In</Label>
          <Label>
            Identifying data associated with your login information will
            be stored by SmartWiFi and accessible to {clientName}.
          </Label>
          <Label>Note: You may opt out again in the future.</Label>
        </div>
      </ConfirmModal>
      <ConfirmModal
        isOpen={state.optOutOpen}
        toggle={() => updateState({ optOutOpen: false })}
        size="lg"
        confirmColor="blue"
        onConfirm={handleOptOut}
      >
        <div align="center">
          <Label tag="h2">Confirm GDPR Opt Out</Label>
          <Label>
            All identifying data associated with your account will be deleted.
            You will still be able to log into SmartWiFi with your current credentials.
          </Label>
          <Label>You can opt in again in the future.</Label>
        </div>
      </ConfirmModal>
      <ConfirmModal
        isOpen={state.deleteOpen}
        toggle={() => updateState({ deleteOpen: false })}
        size="lg"
        confirmColor="red"
        onConfirm={handleDelete}
      >
        <div align="center">
          <Label tag="h2">Confirm Delete Account</Label>
          <Label>All your account with {clientName} will be permanently deleted.</Label>
          <br />
          <Label className="fw-bold">This action cannot be undone.</Label>
        </div>
      </ConfirmModal>
    </div>
  );
};

export const userActivityTypes = {
  "none": { cloudKey: 0, description: "Unknown" },
  "login": { cloudKey: 1, description: "Logged in" },
  "logout": { cloudKey: 2, description: "Logged out" },
  "idle": { cloudKey: 3, description: "Went idle" },
  "inactive": { cloudKey: 4, description: "Went inactive" },
};

export default DataView;
